import React, { useCallback, useEffect, useState } from 'react';
import { listBranches, switchBranch, BranchOrgRow, isPaymentsConfigured } from '../services/paymentsApi';
import { customToast } from '../utils/customToast';

/* ============================================================================
   EnterpriseBranchSwitcher — header control that lists every org in the
   caller's enterprise group and re-points the session at the chosen one.
   Only renders when there is more than one org to switch between.
   ========================================================================== */

export default function EnterpriseBranchSwitcher({
  orgCode,
  onSwitched,
}: {
  orgCode: string | null;
  /** After a successful switch — re-read AuthContext so fleet queries remount. */
  onSwitched: () => Promise<void> | void;
}) {
  const [orgs, setOrgs] = useState<BranchOrgRow[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!orgCode || !isPaymentsConfigured()) { setOrgs([]); return; }
    const res = await listBranches(orgCode);
    if (res.ok && Array.isArray(res.orgs)) setOrgs(res.orgs);
    else setOrgs([]);
  }, [orgCode]);

  useEffect(() => { void load(); }, [load]);

  if (orgs.length < 2 || !orgCode) return null;

  async function handleChange(next: string) {
    if (!next || next === orgCode || busy) return;
    setBusy(true);
    try {
      const res = await switchBranch(next);
      if (!res.ok) {
        customToast.show(res.message || 'Could not switch branch.', 'error');
        return;
      }
      customToast.show(`Switched to ${res.name || next}.`, 'success');
      await onSwitched();
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      title="Switch branch"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px 4px 12px', borderRadius: 999,
        background: '#f3f1fd', border: '1px solid #ddd6fe',
        fontSize: 12, fontWeight: 700, color: '#4c1d95',
        maxWidth: 220,
      }}
    >
      <span style={{ opacity: 0.7, fontWeight: 600, flexShrink: 0 }}>Branch</span>
      <select
        value={orgCode}
        disabled={busy}
        onChange={(e) => { void handleChange(e.target.value); }}
        style={{
          border: 'none', background: 'transparent', font: 'inherit',
          color: 'inherit', fontWeight: 800, cursor: busy ? 'wait' : 'pointer',
          outline: 'none', maxWidth: 140, minWidth: 0,
        }}
      >
        {orgs.map((o) => (
          <option key={o.code} value={o.code}>
            {o.name}{o.isMother ? ' · HQ' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
